/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// chips-io.js — the 65xx peripheral-interface wave: the WDC W65C21 PIA and
// W65C22 VIA, the CMOS 6521/6522. These are microprocessor peripherals, not
// TTL logic: a register file addressed over a bus (RSx / CSx / RWB / PHI2 /
// D0–D7) plus bidirectional I/O ports and, on the VIA, two timers and a shift
// register. Behavior is DATA referencing the pure state machines in
// sim/w65c21.js / sim/w65c22.js — the same "genuine per-part code behind the
// standard sequential contract" pattern the HD44780 uses. Datasheet-exact
// 40-pin DIP pinouts (WDC W65C21 / W65C22 data sheets, Figure 2 / Figure 3-1).
//
// The bus can be driven BY HAND: set the address (RSx), chip selects, RWB, and
// (for a write) D0–D7, then pulse PHI2 — the transfer commits on PHI2's falling
// edge, exactly as the LCD latches on E. Or wire a CPU to it: the W65C02 in
// catalog/chips-cpu.js drives precisely this protocol.

import { w65c21Unit } from "../sim/w65c21.js";
import { w65c22Unit } from "../sim/w65c22.js";
import { input, output, io, gnd, vcc } from "./pin-builders.js";

export const CHIPS_IO = Object.freeze([
  {
    id: "W65C21",
    title: "W65C21 PIA",
    blurb:
      "WDC W65C21 Peripheral Interface Adapter (CMOS 6521/6821): two 8-bit " +
      "bidirectional ports (PA/PB) with per-line data-direction registers, plus " +
      "four handshake/interrupt control lines (CA1/CA2, CB1/CB2). Address a " +
      "register with RS0/RS1 (+ control-register bit 2), select the chip with " +
      "CS0·CS1 high and CS2B low, set RWB, and pulse PHI2 — writes latch on " +
      "PHI2's falling edge. IRQAB/IRQBB are open-drain (wire a pull-up).",
    group: "Interface",
    package: "DIP-40",
    pins: [
      gnd(1, "VSS"),
      io(2, "PA0"),
      io(3, "PA1"),
      io(4, "PA2"),
      io(5, "PA3"),
      io(6, "PA4"),
      io(7, "PA5"),
      io(8, "PA6"),
      io(9, "PA7"),
      io(10, "PB0"),
      io(11, "PB1"),
      io(12, "PB2"),
      io(13, "PB3"),
      io(14, "PB4"),
      io(15, "PB5"),
      io(16, "PB6"),
      io(17, "PB7"),
      input(18, "CB1"),
      io(19, "CB2"),
      vcc(20, "VDD"),
      input(21, "RWB"),
      input(22, "CS0"),
      input(23, "CS2B"),
      input(24, "CS1"),
      input(25, "PHI2"),
      io(26, "D7"),
      io(27, "D6"),
      io(28, "D5"),
      io(29, "D4"),
      io(30, "D3"),
      io(31, "D2"),
      io(32, "D1"),
      io(33, "D0"),
      input(34, "RESB"),
      input(35, "RS1"),
      input(36, "RS0"),
      output(37, "IRQBB"),
      output(38, "IRQAB"),
      io(39, "CA2"),
      input(40, "CA1"),
    ],
    logic: w65c21Unit({
      pa: [2, 3, 4, 5, 6, 7, 8, 9],
      pb: [10, 11, 12, 13, 14, 15, 16, 17],
      d: [33, 32, 31, 30, 29, 28, 27, 26], // D0…D7
      ca1: 40,
      ca2: 39,
      cb1: 18,
      cb2: 19,
      rs0: 36,
      rs1: 35,
      cs0: 22,
      cs1: 24,
      cs2b: 23,
      rwb: 21,
      phi2: 25,
      resb: 34,
      irqab: 38,
      irqbb: 37,
    }),
    pinGroups: [
      { name: "PA", pins: [2, 3, 4, 5, 6, 7, 8, 9], dir: "io" },
      { name: "PB", pins: [10, 11, 12, 13, 14, 15, 16, 17], dir: "io" },
      { name: "D", pins: [33, 32, 31, 30, 29, 28, 27, 26], dir: "io" },
    ],
  },
  {
    id: "W65C22",
    title: "W65C22 VIA",
    blurb:
      "WDC W65C22 Versatile Interface Adapter (CMOS 6522): two 8-bit " +
      "bidirectional ports (PA/PB) with data-direction registers, two 16-bit " +
      "interval timers (T1/T2), an 8-bit shift register, and CA1/CA2/CB1/CB2 " +
      "handshake lines. Address one of 16 registers with RS0–RS3, select with " +
      "CS1 high and CS2B low, set RWB, and pulse PHI2 (writes latch on the " +
      "falling edge). Timers count PHI2 cycles; IRQB is open-drain. Logic-level " +
      "only — no wall-clock timing.",
    group: "Interface",
    package: "DIP-40",
    pins: [
      gnd(1, "VSS"),
      io(2, "PA0"),
      io(3, "PA1"),
      io(4, "PA2"),
      io(5, "PA3"),
      io(6, "PA4"),
      io(7, "PA5"),
      io(8, "PA6"),
      io(9, "PA7"),
      io(10, "PB0"),
      io(11, "PB1"),
      io(12, "PB2"),
      io(13, "PB3"),
      io(14, "PB4"),
      io(15, "PB5"),
      io(16, "PB6"),
      io(17, "PB7"),
      io(18, "CB1"),
      io(19, "CB2"),
      vcc(20, "VDD"),
      output(21, "IRQB"),
      input(22, "RWB"),
      input(23, "CS2B"),
      input(24, "CS1"),
      input(25, "PHI2"),
      io(26, "D7"),
      io(27, "D6"),
      io(28, "D5"),
      io(29, "D4"),
      io(30, "D3"),
      io(31, "D2"),
      io(32, "D1"),
      io(33, "D0"),
      input(34, "RESB"),
      input(35, "RS3"),
      input(36, "RS2"),
      input(37, "RS1"),
      input(38, "RS0"),
      io(39, "CA2"),
      input(40, "CA1"),
    ],
    logic: w65c22Unit({
      pa: [2, 3, 4, 5, 6, 7, 8, 9],
      pb: [10, 11, 12, 13, 14, 15, 16, 17],
      d: [33, 32, 31, 30, 29, 28, 27, 26], // D0…D7
      ca1: 40,
      ca2: 39,
      cb1: 18,
      cb2: 19,
      rs: [38, 37, 36, 35], // RS0, RS1, RS2, RS3
      cs1: 24,
      cs2b: 23,
      rwb: 22,
      phi2: 25,
      resb: 34,
      irqb: 21,
    }),
    pinGroups: [
      { name: "PA", pins: [2, 3, 4, 5, 6, 7, 8, 9], dir: "io" },
      { name: "PB", pins: [10, 11, 12, 13, 14, 15, 16, 17], dir: "io" },
      { name: "D", pins: [33, 32, 31, 30, 29, 28, 27, 26], dir: "io" },
    ],
  },
]);
